import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import {
    ArrowDownIcon,
    CopyIcon,
    DownloadIcon,
    EditIcon,
    LinkIcon,
    PlusIcon,
    TrashIcon,
} from "@icons";
import { OpenMenuCell } from "./MenuPlaygroundCell";
import { MenuPlaygroundContext } from "./MenuPlaygroundContext";
import { StatefulToggleItem } from "./MenuPlaygroundStateful";
import { MenuPlaygroundSelection } from "./MenuPlaygroundSelection";
import { MenuPlaygroundStates } from "./MenuPlaygroundStates";
import { MenuPlaygroundWrappers } from "./MenuPlaygroundWrappers";
import { VariantGrid, VariantItem } from "./playgroundControls";
import styles from "./MenuPlayground.module.scss";

const noop = () => undefined;

const FigmaExamples = () => (
    <VariantGrid title="Figma examples (3831:9252)" columns="three">
        <OpenMenuCell label="3967:1124 · simple">
            <Menu.Item data-forced-state="hover">Simple item 1</Menu.Item>
            <Menu.Item>Simple item 2</Menu.Item>
            <Menu.Item>Simple item 3</Menu.Item>
            <Menu.Item>Simple item 4</Menu.Item>
        </OpenMenuCell>
        <OpenMenuCell label="3965:5937 · toggle + delete (no search, Q9)">
            <Menu.Item data-forced-state="hover">Label item 1</Menu.Item>
            <Menu.Item>Label item 2</Menu.Item>
            <Menu.Item>Label item 3</Menu.Item>
            <Menu.Item>Label item really long name 4</Menu.Item>
            <Menu.Separator />
            <StatefulToggleItem initial>Label item switch</StatefulToggleItem>
            <Menu.Separator />
            <Menu.Item destructive>Delete</Menu.Item>
        </OpenMenuCell>
    </VariantGrid>
);

const Triggers = () => (
    <VariantGrid title="Triggers" columns="three">
        <VariantItem label="Menu.More · secondary (default)">
            <Menu.More>
                <Menu.Item leading={<EditIcon />}>Edit</Menu.Item>
                <Menu.Item leading={<TrashIcon />} destructive>
                    Delete
                </Menu.Item>
            </Menu.More>
        </VariantItem>
        <VariantItem label='Menu.More · primary, aria-label="Invoice actions"'>
            <Menu.More variant="primary" aria-label="Invoice actions">
                <Menu.Item leading={<EditIcon />}>Edit</Menu.Item>
                <Menu.Item leading={<TrashIcon />} destructive>
                    Delete
                </Menu.Item>
            </Menu.More>
        </VariantItem>
        <VariantItem label="Menu.Root · labelled Button">
            <Menu.Root
                trigger={
                    <Button variant="primary" leftIcon={<DownloadIcon />}>
                        Export
                    </Button>
                }
            >
                <Menu.Item>CSV</Menu.Item>
                <Menu.Item>Excel</Menu.Item>
            </Menu.Root>
        </VariantItem>
        <VariantItem label="Menu.Trigger · non-button element">
            <Menu.Root
                trigger={
                    <Menu.Trigger
                        nativeButton={false}
                        render={
                            <span className={styles.chipTrigger}>
                                Kick Design Studio
                                <ArrowDownIcon aria-hidden />
                            </span>
                        }
                    />
                }
            >
                <Menu.Item>Switch entity</Menu.Item>
                <Menu.Item>Entity settings</Menu.Item>
            </Menu.Root>
        </VariantItem>
    </VariantGrid>
);

const Rows = () => (
    <VariantGrid title="Rows" columns="three">
        <OpenMenuCell label="leading icons · right text">
            <Menu.Item leading={<EditIcon />} rightText="E">
                Edit
            </Menu.Item>
            <Menu.Item leading={<CopyIcon />} rightText="3 lines">
                Duplicate
            </Menu.Item>
            <Menu.Item leading={<PlusIcon />}>Add note</Menu.Item>
        </OpenMenuCell>
        <OpenMenuCell label="descriptions">
            <Menu.Item description="Every line of the invoice, one row each">
                Export CSV
            </Menu.Item>
            <Menu.Item
                leading={<DownloadIcon />}
                description="Printable copy with your logo"
            >
                Export PDF
            </Menu.Item>
        </OpenMenuCell>
        <OpenMenuCell label="groups with labels">
            <Menu.Group>
                <Menu.GroupLabel>Invoice</Menu.GroupLabel>
                <Menu.Item>Edit</Menu.Item>
                <Menu.Item>Duplicate</Menu.Item>
            </Menu.Group>
            <Menu.Group>
                <Menu.GroupLabel>Customer</Menu.GroupLabel>
                <Menu.Item>Open customer</Menu.Item>
            </Menu.Group>
            <Menu.Separator />
            <Menu.Item destructive>Delete</Menu.Item>
        </OpenMenuCell>
        <OpenMenuCell label="disabled with reason (hover it) · destructive">
            <Menu.Item leading={<EditIcon />} onClick={noop}>
                Edit
            </Menu.Item>
            <Menu.Item
                leading={<CopyIcon />}
                disabled
                disabledReason="Only admins can duplicate invoices"
            >
                Duplicate
            </Menu.Item>
            <Menu.Item leading={<TrashIcon />} destructive>
                Delete
            </Menu.Item>
        </OpenMenuCell>
        <OpenMenuCell label="Menu.LinkItem · to / href">
            <Menu.LinkItem leading={<LinkIcon />} to="/design-system/picker">
                Picker playground
            </Menu.LinkItem>
            <Menu.LinkItem
                leading={<LinkIcon />}
                href="https://base-ui.com/react/components/menu"
                target="_blank"
            >
                Base UI docs
            </Menu.LinkItem>
        </OpenMenuCell>
    </VariantGrid>
);

/**
 * Static sections under the live stage: the Figma examples recreated for
 * side-by-side screenshots, every trigger, every row shape, submenus and
 * selection rows, the design-system wrappers, and the state matrix. Popups
 * are forced open under their own trigger.
 */
export const MenuPlaygroundExamples = () => (
    <>
        <FigmaExamples />
        <Triggers />
        <MenuPlaygroundContext />
        <Rows />
        <MenuPlaygroundSelection />
        <MenuPlaygroundWrappers />
        <MenuPlaygroundStates />
    </>
);
